import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp, query, where, orderBy, getDocs, onSnapshot, updateDoc, doc } from 'firebase/firestore';

export type NotificationType = 'view' | 'order' | 'system' | 'message' | 'wallet';

export interface Notification {
  id?: string;
  userId: string;
  type: NotificationType;
  title: string;
  body: string;
  listingId?: string;
  createdAt?: any; // Firestore Timestamp
  read: boolean;
  metadata?: Record<string, any> | null;
}

export async function createNotification(userId: string, payload: Omit<Notification, 'userId' | 'createdAt' | 'read'> & { read?: boolean }): Promise<string> {
  const ref = await addDoc(collection(db, 'notifications'), {
    userId,
    type: payload.type,
    title: payload.title,
    body: payload.body,
    listingId: payload.listingId ?? null,
    createdAt: serverTimestamp(),
    read: payload.read ?? false,
    metadata: payload.metadata ?? null,
  });
  return ref.id;
}

export async function getNotifications(userId: string): Promise<Notification[]> {
  try {
    const q = query(collection(db, 'notifications'), where('userId', '==', userId), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...(d.data() as Notification) }));
  } catch (e: any) {
    // Fallback if orderBy index missing
    const qNoOrder = query(collection(db, 'notifications'), where('userId', '==', userId));
    const snap = await getDocs(qNoOrder);
    const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as Notification) }));
    items.sort((a, b) => ((b as any).createdAt?.seconds || 0) - ((a as any).createdAt?.seconds || 0));
    return items;
  }
}

export async function getUnreadCount(userId: string): Promise<number> {
  const q = query(collection(db, 'notifications'), where('userId', '==', userId), where('read', '==', false));
  const snap = await getDocs(q);
  return snap.size;
}

export function subscribeUnreadNotifications(userId: string, cb: (count: number) => void): () => void {
  const q = query(collection(db, 'notifications'), where('userId', '==', userId), where('read', '==', false));
  return onSnapshot(q, (snapshot) => {
    cb(snapshot.size);
  });
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  const ref = doc(db, 'notifications', notificationId);
  await updateDoc(ref, { read: true });
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  const q = query(collection(db, 'notifications'), where('userId', '==', userId), where('read', '==', false));
  const snap = await getDocs(q);
  const updates: Promise<void>[] = [];
  snap.forEach(d => {
    updates.push(updateDoc(d.ref, { read: true }));
  });
  await Promise.all(updates);
}